import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { authAPI } from '../../services/api';
import { selectIsAuthenticated } from '../../store/slices/authSlice';
import { useTheme } from '../../contexts/ThemeContext';

const languages = [
  { code: 'hr', flag: '🇭🇷', name: 'Hrvatski', nameKey: 'language.croatian' },
  { code: 'en', flag: '🇬🇧', name: 'English', nameKey: 'language.english' },
  { code: 'de', flag: '🇩🇪', name: 'Deutsch', nameKey: 'language.german' }
];

const LanguageSwitcher = ({ className = '' }) => {
  const { i18n, t } = useTranslation('common');
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  
  const currentLangCode = i18n.language?.split('-')[0] || 'hr';
  const currentLang = languages.find(l => l.code === currentLangCode) || languages[0];
  
  const changeLanguage = async (langCode) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);

    // Sync language preference to backend for push notification localization
    if (isAuthenticated) {
      try {
        await authAPI.updateLanguage(langCode);
      } catch (err) { /* non-blocking */ }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
        title={t('language.selectLanguage', 'Select language')}
      >
        <span className="text-lg">{currentLang.flag}</span>
        <ChevronDownIcon className={`h-3 w-3 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                currentLang.code === lang.code ? `${theme.accentLight} ${theme.badgeText}` : 'text-gray-700'
              }`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span className="font-medium">{lang.name}</span>
              {currentLang.code === lang.code && (
                <span className={`ml-auto ${theme.headerAccent}`}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;

